import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { getAPIBase } from "@/core/config";

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;
const AUTO_REFRESH_COOLDOWN = 24 * 60 * 60 * 1000;
const LAST_REFRESH_KEY = "omnos_last_auto_refresh";

export function useVersionCheck() {
  const [versionMismatch, setVersionMismatch] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);
  const [canAutoRefresh, setCanAutoRefresh] = useState(true);
  const location = useLocation();

  const parseVersion = version => {
    if (!version) return null;
    const cleaned = version.replace(/^v/, "");
    const parts = cleaned.split(".");
    if (parts.length !== 3) return null;

    const major = parseInt(parts[0], 10);
    const minor = parseInt(parts[1], 10);
    const patch = parseInt(parts[2], 10);

    if (isNaN(major) || isNaN(minor) || isNaN(patch)) return null;

    return { major, minor, patch };
  };

  const versionsMatch = (v1, v2) => {
    const parsed1 = parseVersion(v1);
    const parsed2 = parseVersion(v2);

    if (!parsed1 || !parsed2) {
      return true;
    }

    return (
      parsed1.major === parsed2.major && parsed1.minor === parsed2.minor && parsed1.patch === parsed2.patch
    );
  };

  const frontendVersion = import.meta.env.VITE_VERSION || "0.0.0";

  const checkLastRefreshTime = () => {
    try {
      const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY);
      if (!lastRefresh) {
        return true;
      }
      const timeSinceRefresh = Date.now() - parseInt(lastRefresh, 10);
      return timeSinceRefresh > AUTO_REFRESH_COOLDOWN;
    } catch (e) {
      return false;
    }
  };

  const checkVersion = async () => {
    try {
      const response = await fetch(`${getAPIBase()}/api/get-version/`);
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      const backend = data.version;
      setBackendVersion(backend);

      const parsedBackend = parseVersion(backend);
      const parsedFrontend = parseVersion(frontendVersion);
      const match = versionsMatch(backend, frontendVersion);

      console.log("Version check:", {
        backend,
        frontend: frontendVersion,
        parsedBackend,
        parsedFrontend,
        match
      });

      if (!match) {
        setVersionMismatch(true);
        setCanAutoRefresh(checkLastRefreshTime());
      } else {
        setVersionMismatch(false);
        try {
          localStorage.removeItem(LAST_REFRESH_KEY);
        } catch (e) {
          console.warn("Could not clear refresh timestamp:", e);
        }
      }
    } catch (error) {
      console.warn("Version check failed:", error);
    }
  };

  useEffect(() => {
    checkVersion();

    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    checkVersion();
  }, [location.pathname]);

  return {
    versionMismatch,
    backendVersion,
    frontendVersion,
    canAutoRefresh
  };
}

